/**
 * Operator session expiry — the pure half, so it can be tested without React.
 *
 * WHAT THIS CLOSES (multi-environment closure, 7 Sep 2026, owner item 18)
 * ----------------------------------------------------------------------
 * An operator token is an 8-hour absolute JWT (backend
 * `ADMIN_TOKEN_EXPIRE_HOURS`, no refresh endpoint by design — a control-plane
 * session should not slide past a working day unattended). The UI knew none
 * of that: it read `admin_token` from localStorage forever, and the only
 * place a 401 was handled was the capabilities fetch at mount. Every page
 * fetched with the dead token, got a 401, and rendered an error or nothing.
 * The coordinator's session "expired silently" exactly this way.
 *
 * Three pieces, all here, all pure:
 *   - `tokenExpiresAt(token)`: the `exp` claim, read from the JWT payload.
 *     No secret is needed to READ a JWT; only to verify it, which the
 *     backend still does on every request. The client uses it only to know
 *     WHEN to stop presenting the token.
 *   - `isAdminApiRequest(url)`: which 401s mean "your session is gone" —
 *     the Admin API, minus the login route itself (a wrong password is a
 *     401 too, and must not bounce the login page to the login page).
 *   - `rememberReturnPath` / `consumeReturnPath`: the path the operator was
 *     on when the session ended, so signing in again lands them back there
 *     rather than on the dashboard.
 */

export const RETURN_TO_KEY = 'admin_return_to';
export const SESSION_NOTICE_KEY = 'admin_session_notice';

/** Milliseconds since epoch at which the token expires, or null if unreadable. */
export function tokenExpiresAt(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === 'function'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('binary');
    const payload = JSON.parse(json);
    if (typeof payload.exp !== 'number') return null;
    return payload.exp * 1000;
  } catch (_) {
    return null;
  }
}

/** True when the token carries an `exp` that has already passed. */
export function isTokenExpired(token, now = Date.now()) {
  const at = tokenExpiresAt(token);
  return at !== null && at <= now;
}

/**
 * Milliseconds until the token expires, clamped to what `setTimeout` can
 * hold (2^31 - 1), or null if the token carries no readable expiry.
 */
export function msUntilExpiry(token, now = Date.now()) {
  const at = tokenExpiresAt(token);
  if (at === null) return null;
  return Math.max(0, Math.min(at - now, 2147483647));
}

/** A 401 from here means the operator session is gone. */
export function isAdminApiRequest(url) {
  if (!url || typeof url !== 'string') return false;
  if (!/\/api\/admin(\/|$|\?)/.test(url)) return false;
  if (/\/api\/admin\/login(\?|$)/.test(url)) return false;
  if (/\/api\/admin\/setup(\?|$)/.test(url)) return false;
  return true;
}

function storage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

/** Remember where the operator was, unless that is the login page. */
export function rememberReturnPath(path) {
  const s = storage();
  if (!s || !path || path === '/login' || path.startsWith('/login?')) return;
  try { s.setItem(RETURN_TO_KEY, path); } catch (_) { /* private mode */ }
}

/** Read and clear the remembered path. */
export function consumeReturnPath() {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(RETURN_TO_KEY);
    s.removeItem(RETURN_TO_KEY);
    return v && v.startsWith('/') && !v.startsWith('//') ? v : null;
  } catch (_) {
    return null;
  }
}

export function setSessionNotice(reason) {
  const s = storage();
  if (!s) return;
  try {
    if (reason) s.setItem(SESSION_NOTICE_KEY, reason);
    else s.removeItem(SESSION_NOTICE_KEY);
  } catch (_) { /* private mode */ }
}

export function consumeSessionNotice() {
  const s = storage();
  if (!s) return null;
  try {
    const v = s.getItem(SESSION_NOTICE_KEY);
    s.removeItem(SESSION_NOTICE_KEY);
    return v || null;
  } catch (_) {
    return null;
  }
}

export const NOTICE_TEXT = {
  expired: 'Your session has expired. Sign in again to continue where you left off.',
  revoked: 'Your session was signed out. Sign in again to continue.',
};
